import $ from 'jquery';
import { renderTpl, formatCurrency, uniqueListener, compileTemplates, momentToRemote, momentToRemoteLong, momentToAPI, parsePublishAtInput, localePrefix } from './helpers.js';
import { API } from './api.js';
import Cookies from 'js-cookie';
import { RichEditor } from './rich-editor.js';
import { Notifier } from './notifier.js';
import { MediaGallery } from './media-gallery.js';
import { updateDatePickerInfo } from './app.js'

const stripeStyle = {
  base: {
    color: '#32325d',
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4'
    }
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a'
  }
};

const beforeUnloadHandler = function (e) {
  e.preventDefault();
  e.returnValue = '';
};

// The press contact, in the order the card asks for it and the preview tile lists it. The names
// are the field-map keys and the input names both — they are what the server is sent.
const CONTACT_FIELDS = ['contact_name', 'contact_phone', 'contact_email'];

// Preview step (step 2) ————————————————————————————————————————————————————————
//
// The preview shows the manuscript the browser is holding, not the one the server has. The title,
// the dateline and the introduction are written with `textContent`, so nothing in them can be
// markup. The body genuinely is HTML (Quill writes it), so it is parsed detached first — DOMParser
// runs no script and fetches nothing — stripped of the elements and attributes that could execute,
// and only the result reaches `innerHTML`. The authoritative rendering of a release stays the
// server's own preview (app/views/releases/preview.html.erb), which is stricter than this.

const PREVIEW_STRIPPED_TAGS = 'script, iframe, object, embed, link, meta, style, base, form';

// The EN shape of `DTFormatDisplayDate` (helpers.js) — `Oct 9, 2026` — pinned for the dateline.
const PREVIEW_DATELINE_FORMAT = 'MMM D, YYYY';

const scrubPreviewBody = (html) => {
  const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html').body;

  Array.from(parsed.querySelectorAll(PREVIEW_STRIPPED_TAGS)).forEach(node => node.remove());

  Array.from(parsed.querySelectorAll('*')).forEach(node => {
    Array.from(node.attributes).forEach(({ name, value }) => {
      const isEventHandler = name.toLowerCase().indexOf('on') === 0;
      const isScriptUrl = /^(href|src|xlink:href)$/i.test(name) && /^\s*javascript:/i.test(value);
      if (isEventHandler || isScriptUrl) node.removeAttribute(name);
    });
  });

  return parsed;
};

const plainTextFromHtml = (html) =>
  new DOMParser().parseFromString(String(html || ''), 'text/html').body.textContent.trim();

// Empty fields keep the muted placeholder the view rendered into them, so the preview never shows
// a blank slot with no explanation.
const setPreviewText = (elem, text) => {
  if (!elem) return;
  elem.textContent = text || elem.dataset.empty || '';
  elem.classList.toggle('release-preview-empty', !text);
};

export class prCreator {
  static instances = [];

  static init() {
    const wrapper = document.querySelector('.pr-creator');
    if (wrapper && !this.instances.some(instance => instance.wrapper === wrapper)) {
      const newInstance = new prCreator(wrapper);
      this.instances.push(newInstance);
      return newInstance;
    } else {
      return undefined;
    }
  }

  constructor(wrapper) {
    this.dom = {
      wrapper
    };
    this.leavingPrevented = false;
    // Hydration arms the unsaved-changes guard by itself (initOrder → togglePlan on the plan the
    // server preselected). That is a page the reader has not touched, so the editor bar must not
    // report it as an edit — the flag keeps the draft state silent until hydration is over.
    this.hydrating = true;

    this.initFields();
    this.initDOM();
    this.initNotifier();
    this.initMediaGallery();
    this.initTemplates();
    this.initEditors();
    this.initUploaders();
    this.initCounters();
    this.initRegionAccordions();
    this.initOrder();
    this.initTabs();

    if (window._stripe_public_key) {
      this.stripe = Stripe(window._stripe_public_key);
      this.stripeElements = this.stripe.elements();
    }

    this.hydrating = false;
  };

  gTagEvent(category, action, label, value) {
    const tag = { category, action, label };
    if (typeof value === 'number') tag.value = value;
    try {
      gtag('event', 'frontend', tag);
    } catch (e) {
      console.warn('Cannot set gtag event:', tag);
    }
  };

  // The editor bar's draft state. It is persistent: the bar is seeded with what the server knows
  // (layouts/_editor_bar) and from then on says either when the draft was saved or that the
  // manuscript has moved past that save. It never goes back to saying nothing.
  //
  // The seed can be the "not saved yet" placeholder, which wears a class that takes the line's icon
  // away. Whatever this writes replaces that state, so the class goes with it — otherwise the
  // pencil "Unsaved changes" earns would stay suppressed.
  setDraftStatus(text = '', { unsaved = false } = {}) {
    const status = this.dom.draftStatus;
    if (!status) return;

    status.textContent = text;
    status.classList.remove('editor-bar-status-placeholder');
    status.classList.toggle('editor-bar-status-unsaved', Boolean(text) && unsaved);
  };

  showDraftSaved() {
    if (!this.dom.draftStatus) return;

    this.setDraftStatus(this.dom.draftStatus.dataset.draftSavedLabel || '');
  };

  showUnsavedChanges() {
    if (!this.dom.draftStatus || this.hydrating) return;

    this.setDraftStatus(this.dom.draftStatus.dataset.unsavedLabel || '', { unsaved: true });
  };

  preventLeaving(lock = true) {
    if (lock && !this.leavingPrevented) {
      this.leavingPrevented = true;
      this.dom.saveDraft.classList.add('active');
      this.showUnsavedChanges();
      window.addEventListener('beforeunload', beforeUnloadHandler);
    } else if (!lock && this.leavingPrevented) {
      this.leavingPrevented = false;
      this.dom.saveDraft.classList.remove('active');
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  };

  initFields() {
    this.fields = {
      title: { checkoutSection: 'content', tab: 'content', errors: [] },
      introduction: { checkoutSection: 'content', tab: 'content', errors: [] },
      body: { checkoutSection: 'content', tab: 'content', errors: [] },
      // The press contact is three fields since the split — name, phone, email — and all three
      // live in step 1 with their card. The tab name has to stay `content` or the step lets a
      // blank contact through and the error only surfaces at checkout. `contact_info` is gone
      // from here with the textarea it named: the server no longer accepts it, and an
      // `.error-msg[for=contact_info]` left behind would throw in refreshErrors. The two
      // checkboxes carried a `checkoutSection: 'media-kit'` that never matched a section of
      // `this.order` — dead since the checkout summary was written, dropped here.
      contact_name: { checkoutSection: 'publish_at', tab: 'content', errors: [] },
      contact_phone: { checkoutSection: 'publish_at', tab: 'content', errors: [] },
      contact_email: { checkoutSection: 'publish_at', tab: 'content', errors: [] },
      save_contact_info_in_user: { tab: 'content', errors: [] },
      contact_info_not_public: { tab: 'content', errors: [] },
      publish_at: { checkoutSection: 'publish_at', tab: 'publishing', errors: [] },
      skip_approval: { checkoutSection: 'publish_at', tab: 'publishing', errors: [] },
      categories: { checkoutSection: 'categories', tab: 'categories', errors: [] },
      options: { checkoutSection: 'options', tab: 'options', errors: [] },
      plans: { checkoutSection: 'plans', tab: 'options', errors: [] },
      manual_payment: { tab: 'checkout', errors: [] },
      invoice_data_company_name: { tab: 'checkout', errors: [] },
      invoice_data_department: { tab: 'checkout', errors: [] },
      invoice_data_full_name: { tab: 'checkout', errors: [] },
      invoice_data_location: { tab: 'checkout', errors: [] },
      invoice_data_phone: { tab: 'checkout', errors: [] },
      invoice_data_email: { tab: 'checkout', errors: [] },
      save_invoice_data_in_user: { tab: 'checkout', errors: [] },
      // One consent box now covers all three acknowledgments it lists (owner decision 2026-08-21).
      // `guidelines_accepted` / `changes_payment_accepted` went with the two inputs: the keys of
      // this map and of `this.dom.fields` have to describe the same set of controls — refreshErrors
      // reads `this.fields[name]` for every `.error-msg[for]` node it finds.
      terms_accepted: { tab: 'checkout', errors: [] },
    };
  };

  initDOM() {
    this.dom.arrowsPrev = Array.from(this.dom.wrapper.querySelectorAll('.creator-prev-tab'));
    this.dom.arrowsNext = Array.from(this.dom.wrapper.querySelectorAll('.creator-next-tab'));
    // Signed in, the save-as-draft control is in the editor bar (layouts/_editor_bar), which is part
    // of the page header and so outside the creator's wrapper; signed out it is still in the price
    // bar inside it. Either way there is exactly one on the page.
    this.dom.saveDraft = this.dom.wrapper.querySelector('.creator-save-draft')
      || document.querySelector('.creator-save-draft');
    this.dom.draftStatus = document.querySelector('[data-creator-status]');
    this.dom.tierChip = document.querySelector('[data-creator-tier]');
    this.dom.finalizeOrder = this.dom.wrapper.querySelector('.creator-finalize-order');

    this.dom.orderId = this.dom.wrapper.querySelector('[name="id"]');

    this.dom.contentSections = {
      editor: this.dom.wrapper.querySelector('section.editor'),
      contentFile: this.dom.wrapper.querySelector('section.content-file'),
      contentDefaultHeading: this.dom.wrapper.querySelector('.content-default-heading'),
      contentUploadHeading: this.dom.wrapper.querySelector('.content-upload-heading'),
      contentUploadedHeading: this.dom.wrapper.querySelector('.content-uploaded-heading'),
    }
    this.dom.contentForm = this.dom.wrapper.querySelector('form.editor-text');


    this.dom.tabs = Array.from(this.dom.wrapper.querySelectorAll('.tab-link .tab')).reduce((result, elem) => ({ ...result, [elem.id.replace('-tab', '')]: elem }), {});

    this.dom.fields = Object.fromEntries(Object.keys(this.fields).map(name => [name, this.dom.wrapper.querySelector(`[name="${name}"]`)]).filter(([name, entry]) => entry));
    this.dom.errorMsgs = Array.from(this.dom.wrapper.querySelectorAll('.error-msg[for]')).reduce((result, elem) => ({ ...result, [elem.getAttribute('for')]: elem }), {});

    // Rendered only for a release still carrying the pre-split free-text contact (form/_content).
    // It is the sole copy of that text on the page, so the preview tile reads it from here.
    this.dom.legacyContact = this.dom.wrapper.querySelector('[data-legacy-contact]');

    // this.dom.uploaderMediaKit = this.dom.wrapper.querySelector('.uploader-media-kit');
    this.dom.mediaKitList = this.dom.wrapper.querySelector('.media-kit-list');
    this.dom.mediaKitDropZone = this.dom.wrapper.querySelector('.media-kit-dropzone');

    this.dom.preview = {
      panel: this.dom.wrapper.querySelector('#preview-panel'),
      title: this.dom.wrapper.querySelector('.release-preview-title'),
      dateline: this.dom.wrapper.querySelector('.release-preview-dateline'),
      intro: this.dom.wrapper.querySelector('.release-preview-intro'),
      body: this.dom.wrapper.querySelector('.release-preview-body'),
      media: this.dom.wrapper.querySelector('.release-preview-media'),
      mediaImage: this.dom.wrapper.querySelector('.release-preview-media-image'),
      contact: this.dom.wrapper.querySelector('.preview-media-contact'),
      contactBody: this.dom.wrapper.querySelector('.preview-media-contact-body'),
      mediaKit: this.dom.wrapper.querySelector('.preview-media-kit'),
      // `.preview-media-kit-list`, not `.media-kit-list`: the latter is resolved document-wide and
      // the first match wins, so a second node carrying it would take the uploader's target.
      mediaKitList: this.dom.wrapper.querySelector('.preview-media-kit-list'),
      mediaKitRowTpl: this.dom.wrapper.querySelector('.tpl-preview-media-kit-file'),
    };

    // The preview's "Edit content" button and the categories step's "Upgrade plan" button ride the
    // wizard's own step-navigation hook. The summary and checkout tables bind theirs after every
    // Handlebars render; these are static markup and are bound once, here.
    const staticTabTriggers = '#preview-panel [data-target-tab], #categories-panel [data-target-tab], #checkout-panel .checkout-upsell [data-target-tab]';
    Array.from(this.dom.wrapper.querySelectorAll(staticTabTriggers)).forEach(trigger => {
      uniqueListener(trigger, 'click', (e) => {
        e.preventDefault();
        $(this.dom.tabs[trigger.getAttribute('data-target-tab')]).tab('show');
      });
    });

    this.dom.categoryFilterField = this.dom.wrapper.querySelector('#category-filter-field');
    this.dom.categoryRegions = this.dom.wrapper.querySelectorAll('.region-group');
    this.dom.categoryCounter = {
      selected: this.dom.wrapper.querySelector('.category-counter-selected'),
      included: this.dom.wrapper.querySelector('.category-counter-included'),
      freeCount: this.dom.wrapper.querySelector('.category-free-count'),
    };
    this.dom.addonFooter = this.dom.wrapper.querySelector('.addon-card-footer');
    this.dom.addonCount = this.dom.wrapper.querySelector('.addon-selected-count');
    this.dom.addonTotal = this.dom.wrapper.querySelector('.addon-selected-total');
    this.dom.titleCounter = this.dom.wrapper.querySelector('.field-counter[data-counter-for="form-title"]');
    this.dom.finalizeLabel = this.dom.wrapper.querySelector('.creator-finalize-label');
    this.dom.categoryParentSelector = '.action-card-parent-category';
    this.dom.categories = this.dom.wrapper.querySelectorAll('a[data-type="category"]');
    this.dom.options = this.dom.wrapper.querySelectorAll('a[data-type="option"]');
    this.dom.plans = this.dom.wrapper.querySelectorAll('a[data-type="plan"]');

    this.dom.paymentMethods = this.dom.wrapper.querySelectorAll('a[data-type="payment-type"]');
    this.dom.invoiceData = this.dom.wrapper.querySelector('.invoice-data');
    this.dom.paymentSecured = this.dom.wrapper.querySelector('.payment-secured');
    this.dom.paymentBlock = this.dom.wrapper.querySelector('[data-checkout-payment]');
    // The checkout step's panel: `checkout-free` on it is what the CSS reads to show the ¥0 shape
    // (`[data-checkout-free-only]`) or the paid one (`[data-checkout-paid-only]`). The panel, not
    // `.checkout`, because the step's heading and subtitle sit above that block.
    this.dom.checkout = this.dom.wrapper.querySelector('#checkout-panel');
    // A release that already has an order (pageData.order.ordered): the step's third shape, set once.
    // The server still decides on the click (CheckoutService); this only stops the page offering a
    // payment that will not be taken.
    this.ordered = Boolean(pageData.order && pageData.order.ordered);
    if (this.dom.checkout) {
      this.dom.checkout.classList.toggle('checkout-ordered', this.ordered);
    }
    // Where a checkout that took nothing says why (showCheckoutRefusal).
    this.dom.checkoutRefusal = this.dom.wrapper.querySelector('.checkout-refusal');

    // "No thanks" on the ¥0 upsell folds the panel for the rest of the visit; nothing is stored.
    const upsell = this.dom.wrapper.querySelector('[data-checkout-upsell]');
    const upsellDismiss = this.dom.wrapper.querySelector('[data-checkout-upsell-dismiss]');
    if (upsell && upsellDismiss) {
      uniqueListener(upsellDismiss, 'click', () => upsell.classList.add('checkout-upsell-dismissed'));
    }

    this.dom.modals = {
      stripe: this.dom.wrapper.querySelector('#modal-stripe'),
      bankTransfer: this.dom.wrapper.querySelector('#modal-manual-payment'),
      inquiry: this.dom.wrapper.querySelector('#modal-inquiry'),
      inquiryConfirmation: this.dom.wrapper.querySelector('#modal-inquiry-confirmation'),
      freeSuccess: this.dom.wrapper.querySelector('#modal-free-success'),
      resubmitted: this.dom.wrapper.querySelector('#modal-resubmitted'),
    };

    this.dom.stripe = {
      form: document.getElementById('stripe-payment-form'),
      button: document.getElementById('stripe-request-button'),
      buttonWrapper: document.getElementById('stipe-button-payment'),
      card: document.getElementById('stripe-card-element'),
      cardErrors: document.getElementById('stripe-card-errors'),
      success: document.getElementById('stripe-success'),
    };

    this.dom.inquiry = {
      triggers: this.dom.wrapper.querySelectorAll('.send-inquiry-btn'),
      msg: this.dom.modals.inquiry.querySelector('[name="inquiry-msg"]'),
      submit: this.dom.modals.inquiry.querySelector('#submit-inquiry'),
    };

    Array.from(this.dom.inquiry.triggers).forEach(trigger => {
      uniqueListener(trigger, 'click', this.openInquiryModal);
    })
    uniqueListener(this.dom.inquiry.submit, 'click', this.submitInquiry);

    uniqueListener(this.dom.contentForm, 'submit', e => e.preventDefault());

    uniqueListener(this.dom.saveDraft, 'click', this.handleSaveClick);
    uniqueListener(this.dom.finalizeOrder, 'click', this.handleFinalizeClick);
    uniqueListener(this.dom.fields.terms_accepted, 'change', this.handleTermsCheckbox);

    Array.from(this.dom.paymentMethods).forEach(radio => {
      uniqueListener(radio, 'change', this.handlePaymentMethod);
    })

    Object.values(this.dom.fields).forEach(field => uniqueListener(field, 'change', () => {
      this.preventLeaving();
      try {
        this.fields[field.name].errors.forEach(err => {
          const index = this.order[this.fields[field.name].checkoutSection].errors.indexOf(err);
          if (index > -1) {
            this.order[this.fields[field.name].checkoutSection].errors.splice(index, 1);
          }
        });
        this.fields[field.name].errors = [];
      } catch (e) { }
      this.refreshErrors();
    }));

    uniqueListener(this.dom.categoryFilterField, 'search', this.handleCategoryFilter);
    uniqueListener(this.dom.categoryFilterField, 'keyup', this.handleCategoryFilter);
    // Pasting and autofill reach the field without a keystroke.
    uniqueListener(this.dom.categoryFilterField, 'input', this.handleCategoryFilter);
    uniqueListener(this.dom.categoryFilterField, 'change', this.handleCategoryFilter);
    Array.from(this.dom.categories).forEach(card => uniqueListener(card, 'click', this.handleCategoryClick));
    Array.from(this.dom.options).forEach(card => uniqueListener(card, 'click', this.handleOptionClick));
    Array.from(this.dom.plans).forEach(card => uniqueListener(card, 'click', this.handlePlanClick));
    Array.from(this.dom.paymentMethods).forEach(card => uniqueListener(card, 'click', this.handlePaymentTypeClick));

    Array.from(document.querySelectorAll('[data-action="save-and-show-modal"]')).forEach(link => {
      uniqueListener(link, 'click', this.handleSignupClick);
    });
  };

  initNotifier() {
    this.notifier = new Notifier();

    // this.notifier.show('upload-progress', {progress: 25});
    // this.notifier.show('upload-success');
    // this.notifier.show('upload-error');
  };

  initEditors() {
    this.editors = {};
    window.dbg = this.editors;

    this.editors.intro = new RichEditor(this.dom.fields.introduction, {
      bindings: {
        tab: {
          key: 9,
          handler: () => this.editors.body.editor.focus(),
        },
        enter: {
          key: 'Enter',
          handler: () => this.editors.body.editor.focus(),
        },
      }
    });
    // The body is the field people write in, so its toolbar is on screen rather than hiding until
    // something is selected: the design's Textarea `toolbar` row
    // (docs/design-system/components/forms/Textarea.jsx), which Quill draws with the `snow` theme.
    // The introduction keeps the selection popover — a lead paragraph needs no formatting row.
    this.editors.body = new RichEditor(this.dom.fields.body, {
      theme: 'snow',
      toolbar: [
        ['bold', 'italic'],
        [{ list: 'bullet' }, { list: 'ordered' }],
        ['link', 'blockquote', 'image'],
      ],
      sideMenu: {
        action: () => this.mediaGallery.open('editor'), // must return a Promise!
        // action: () => this.uploader.uploadImage(), // must return a Promise!
      },
    });

    if (!this.showHideContentSections()) {
      this.dom.fields.title.focus();
    }

  };

  showHideContentSections(argFile) {
    // `||`, not `??`: the upload callback passes `false` for "this batch carried no content file",
    // which is not the same as "there is no content file" — a description save on a release that
    // already has one arrives here as `false` and must not hide the section.
    const contentFile = argFile || this.mediaGallery.uploader.getContentFile();

    if (contentFile) {
      renderTpl(this.templates.contentFile, contentFile);

      Array.from(this.templates.contentFile.target.querySelectorAll('.file-remove')).forEach(elem => {
        elem.addEventListener('click', this.mediaGallery.uploader.handleFileDeletion);
      });

      if (!this.dom.fields.title.value) {
        this.dom.fields.title.value = contentFile.name;
      }
    }

    this.dom.contentSections.contentDefaultHeading.classList.toggle('d-none', !!contentFile);
    this.dom.contentSections.contentUploadHeading.classList.toggle('d-none', !!contentFile);
    this.dom.contentSections.contentUploadedHeading.classList.toggle('d-none', !contentFile);
    this.dom.contentSections.editor.classList.toggle('d-none', !!contentFile);
    this.dom.contentSections.contentFile.classList.toggle('d-none', !contentFile);

    return !!contentFile;
  }

  initMediaGallery() {
    this.mediaGallery = new MediaGallery({
      notifier: this.notifier,
      initialImages: pageData.order.images.map(API.parseFile.fromPageData),
      initialAttachments: pageData.order.attachments.map(API.parseFile.fromPageData),
      initialMediaKit: pageData.order.media_kit,
      onUploadSuccess: (savedFiles) => new Promise((resolve, reject) => {
        const contentFile = savedFiles.attachments.find(file => file.isContent) || false;
        this.showHideContentSections(contentFile && { ...contentFile, library: 'attachments' });

        this.saveOrder('upload-with-data')
          .then(resData => {

            resolve({
              images: JSON.parse(resData.release.images).map(API.parseFile.fromUpdateAPI),
              attachments: JSON.parse(resData.release.attachments).map(API.parseFile.fromUpdateAPI),
              media_kit: JSON.parse(resData.release['media_kit']),
            });
          }).catch((...args) => {
            reject(...args);
            this.showHideContentSections();
          });
      }),
      isFileUsed: (url) => this.editors.body.isFileUsed(url),
      onFileDeletion: (url, file) => {
        this.editors.body.removeFileUses(url);

        if (file.content && this.dom.fields.title.value === file.name) {
          this.dom.fields.title.value = '';
        }
      },
    });

    this.mediaGallery.uploader.initMediaKitDropZone(this.dom.mediaKitDropZone);
  };

  initUploaders() {
    // const listTplElem = document.querySelector('.tpl-attachment-list');
    const contentUploader = document.querySelector('.content-uploader');
    if (!contentUploader) return;

    contentUploader.addEventListener('click', e => {
      e.preventDefault();
      // this.hideContentSection();
      this.mediaGallery.uploadContentFile();
    });
  };

  // Live "N / limit" beside the release title label. The limit is the model's recommended length
  // (Release::TITLE_RECOMMENDED_LENGTH, rendered onto the element) so the two cannot drift; past it
  // the counter turns red, but nothing blocks — the hard limit is validated server-side only.
  initCounters() {
    if (!this.dom.titleCounter || !this.dom.fields.title) return;

    this.titleLimit = parseInt(this.dom.titleCounter.dataset.counterLimit, 10) || 0;
    const render = () => {
      const used = (this.dom.fields.title.value || '').length;
      this.dom.titleCounter.textContent = `${used} / ${this.titleLimit}`;
      this.dom.titleCounter.classList.toggle('field-counter-over', this.titleLimit > 0 && used > this.titleLimit);
    };

    uniqueListener(this.dom.fields.title, 'input', render);
    uniqueListener(this.dom.fields.title, 'change', render);
    render();
  };

  // Distribution lists are grouped into one accordion per region (Accordion.jsx). Collapsing is a
  // class on `.region-group`, never `d-none`: the filter and the plan restrictions own that one.
  initRegionAccordions() {
    this.dom.regionToggles = Array.from(this.dom.wrapper.querySelectorAll('.region-group-toggle'));

    this.dom.regionToggles.forEach(toggle => {
      uniqueListener(toggle, 'click', this.handleRegionToggle);
      uniqueListener(toggle, 'keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        this.handleRegionToggle(e);
      });
    });
  };

  handleRegionToggle = (e) => {
    const group = (e.currentTarget || e.target).closest('.region-group');
    if (!group) return;
    this.setRegionExpanded(group, group.classList.contains('region-group-collapsed'));
  };

  setRegionExpanded(group, expanded) {
    group.classList.toggle('region-group-collapsed', !expanded);
    const toggle = group.querySelector('.region-group-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
  };

  // Keeps at least one region reachable after a filter or a plan restriction has reordered or
  // hidden groups: everything a search matched opens, otherwise the first visible group does.
  refreshRegionAccordions(expandAll = false) {
    // Re-queried rather than read off `dom.categoryRegions`: applyCategoryRestrictions() moves the
    // restricted region to the front, and the cached NodeList keeps the original order.
    const groups = Array.from(this.dom.wrapper.querySelectorAll('.region-group'))
      .filter(group => !group.classList.contains('d-none'));
    if (!groups.length) return;

    groups.forEach((group, index) => {
      const hasSelection = !!group.querySelector('a[data-type="category"].action-card-selected');
      if (expandAll || hasSelection || index === 0) {
        this.setRegionExpanded(group, true);
      }
    });
  };

  // Step 3 footer strip and step 4 counter — AddonRow's "N add-ons selected · +¥X" and the
  // "selected / included" figure beside the distribution-lists heading.
  refreshCommerceMeta(addonsTotal) {
    if (this.dom.addonCount && this.dom.addonFooter) {
      const count = this.order.options.active.length;
      const template = count === 1
        ? this.dom.addonFooter.dataset.labelOne
        : this.dom.addonFooter.dataset.labelOther;
      this.dom.addonCount.textContent = (template || '{count}').replace('{count}', count);
    }

    if (this.dom.addonTotal) {
      this.dom.addonTotal.textContent = `+${formatCurrency(addonsTotal || 0)}`;
    }

    if (this.dom.categoryCounter.selected) {
      this.dom.categoryCounter.selected.textContent = this.order.categories.active.length;
    }

    if (this.dom.categoryCounter.included) {
      this.dom.categoryCounter.included.textContent = pageData.order.freeCategories;
    }

    if (this.dom.categoryCounter.freeCount) {
      this.dom.categoryCounter.freeCount.textContent = pageData.order.freeCategories;
    }
  };

  // The "N selected" chip in each region header. It counts the rendered cards rather than
  // `order.categories.active`, because applyCategoryRestrictions() toggles the selected class on the
  // cards directly (both ways) and the DOM is the state every path ends up agreeing on.
  refreshRegionCounts() {
    this.dom.wrapper.querySelectorAll('.region-group').forEach(group => {
      const chip = group.querySelector('.region-group-count');
      if (!chip) return;

      const count = group.querySelectorAll('a[data-type="category"].action-card-selected').length;
      const template = count === 1 ? chip.dataset.labelOne : chip.dataset.labelOther;
      chip.textContent = (template || '{count}').replace('{count}', count);
      chip.classList.toggle('d-none', count === 0);
    });
  };

  // The ¥0 checkout state.
  //
  // An order that costs nothing has no payment method to choose, nothing to invoice and no
  // first-release discount to apply, and its submit button must not offer to "pay ¥0". This is a
  // reading of the running total, not of the plan: a Free plan with a paid add-on or an extra
  // category costs money and gets the normal checkout back (which is why refreshSummary
  // re-evaluates it on every change rather than the page deciding once).
  //
  // A total of zero with no plan chosen is "not priced yet", not free — but the server always
  // preselects a plan, so `plans.active` is the guard that keeps a half-built order out of here.
  //
  // None of this is authoritative: the client total ignores `custom_release_price`, so how the
  // order is actually taken is decided by the server's `payment_method` in finalizeOrder().
  isZeroTotalOrder() {
    return this.order.plans.active.length > 0 && this.order.total.grossNum === 0;
  };

  refreshZeroTotalState() {
    // An ordered release keeps its own shape whatever its total.
    const free = this.isZeroTotalOrder() && !this.ordered;

    // Read by the summary templates, which drop the first-release-discount line at ¥0.
    this.order.total.free = free;

    // The step's shape: heading, summary, notice, consent list and submit caption all follow it.
    if (this.dom.checkout) {
      this.dom.checkout.classList.toggle('checkout-free', free);
    }

    if (this.dom.paymentBlock) {
      this.dom.paymentBlock.classList.toggle('d-none', free || this.ordered);
    }

    // The lock caption names the card processor. It sits outside the payment cards (under the
    // submit button), so it is switched here on the same rule setPaymentMethod() uses.
    if (this.dom.paymentSecured) {
      this.dom.paymentSecured.classList.toggle('d-none', free || this.ordered || this.order.manual_payment.value);
    }
  };

  // The submit button names the price it is about to charge, and says what is missing until the
  // consent box is ticked (CreatorScreen.jsx, step 6). It sits in the order summary card now, but
  // it is still resolved once from `.pr-creator` and never re-rendered with the price table.
  refreshFinalizeLabel() {
    if (!this.dom.finalizeOrder || !this.dom.finalizeLabel) return;

    const enabled = !this.dom.finalizeOrder.classList.contains('disabled');
    const dataset = this.dom.finalizeOrder.dataset;
    let enabledLabel;
    if (this.ordered && dataset.labelEnabledOrdered) {
      enabledLabel = dataset.labelEnabledOrdered;
    } else if (this.isZeroTotalOrder() && dataset.labelEnabledFree) {
      enabledLabel = dataset.labelEnabledFree;
    } else {
      enabledLabel = (dataset.labelEnabled || '').replace('{amount}', formatCurrency(this.order.total.grossNum || 0));
    }

    this.dom.finalizeLabel.textContent = enabled ? enabledLabel : dataset.labelDisabled;
  };

  // The editor bar's tier chip. The product has a free tier now (a Plan priced at 0), so a zero
  // total on a chosen plan reads as Free rather than as nothing — this supersedes ADR-005 (C).
  // An order with no plan chosen still has no tier at all: that one genuinely is "not priced yet".
  refreshTierChip() {
    if (!this.dom.tierChip) return;

    const priced = this.order.plans.active.length > 0;
    const free = this.isZeroTotalOrder();
    const dataset = this.dom.tierChip.dataset;

    this.dom.tierChip.textContent = priced ? ((free ? dataset.labelFree : dataset.labelPaid) || '') : '';
    this.dom.tierChip.hidden = !priced;
    this.dom.tierChip.classList.toggle('variant-success', free);
    this.dom.tierChip.classList.toggle('variant-info', !free);
  };

  initTemplates() {
    this.templates = {
      contentFile: {
        source: this.dom.wrapper.querySelector('.tpl-content-file-info').innerHTML,
        target: this.dom.contentSections.contentFile,
      },
      summary: {
        source: this.dom.wrapper.querySelector('.summary .tpl-summary-table').innerHTML,
        target: this.dom.wrapper.querySelector('.summary .summary-table'),
      },
      checkout: {
        source: this.dom.wrapper.querySelector('.checkout .tpl-summary-table').innerHTML,
        target: this.dom.wrapper.querySelector('.checkout .summary-table'),
      },
    };
    compileTemplates(this.templates);
  };

  initOrder() {
    this.order = {
      content: { price: 0 },
      categories: { price: 0, active: [], activeAndGroupedByRegion: {} },
      options: { price: 0, active: [] },
      plans: { price: 0, active: [] },
      extraServices: { price: 0, priceVal: 0, active: [] },
      total: { net: 0, tax: 0, gross: 0 },
      publish_at: { date: '' },
      save_contact_info_in_user: { value: false },
      contact_info_not_public: { value: false },
      manual_payment: { value: false },
      translation_enabled: { value: false },
    };

    Object.keys(this.order).forEach(key => this.order[key].errors = []);

    Object.entries(pageData.order.extraServices).forEach(([name, value]) => {
      this.order.extraServices.priceVal += value;
      this.order.extraServices.price = this.order.extraServices.priceVal;
      this.order.extraServices.active.push(name);
    });

    Array.from(this.dom.categories).forEach(elem => {
      if (elem.dataset.active === 'true') {
        elem.classList.add('action-card-selected');
        this.toggleCategory(elem.dataset);
      }
    });

    Array.from(this.dom.options).forEach(elem => {
      if (elem.dataset.active === 'true') {
        elem.classList.add('action-card-selected');
        this.toggleOption(elem.dataset);
      }
    });

    Array.from(this.dom.plans).forEach(elem => {
      if (elem.dataset.active === 'true') {
        elem.classList.add('action-card-selected');
        this.togglePlan(elem.dataset);
      }
    });

    const activePlanElem = Array.from(this.dom.plans).find(p => p.dataset.active === 'true');
    if (activePlanElem) {
      this.applyCategoryRestrictions(parseInt(activePlanElem.dataset.id, 10));
    }

    // The server renders the chosen method as `data-active`; the selected treatment is a class, so
    // the two are reconciled here instead of only on the first click.
    Array.from(this.dom.paymentMethods).forEach(elem => {
      elem.classList.toggle('action-card-selected', elem.dataset.active === 'true');
    });

    this.setPaymentMethod();
    this.refreshSummary();
    this.refreshRegionAccordions();
    this.renderCheckout();
  };

  // The dateline is the scheduled publication date, and only that: the release has no company-name
  // field for one to sit beside. Nothing scheduled yet means no dateline line at all. It belongs to
  // the article, which is English in every UI locale (form/_preview), so it is formatted in English
  // here rather than by the locale-aware `formatDisplayDate`.
  previewDateline() {
    const field = this.dom.fields.publish_at;
    if (!field || !field.value) return '';

    try {
      const publishAt = parsePublishAtInput(field.value);
      return publishAt ? publishAt.clone().locale('en').format(PREVIEW_DATELINE_FORMAT) : '';
    } catch (e) {
      window.debug && console.warn('preview: cannot read publish date:', e);
      return '';
    }
  };

  renderPreview() {
    const preview = this.dom.preview;
    if (!preview || !preview.panel) return;

    setPreviewText(preview.title, (this.dom.fields.title.value || '').trim());

    // Quill keeps the introduction's line breaks, which the plain-text fallback cannot.
    const introText = this.editors && this.editors.intro && this.editors.intro.editor
      ? this.editors.intro.editor.getText().trim()
      : plainTextFromHtml(this.dom.fields.introduction.value);
    setPreviewText(preview.intro, introText);

    this.renderPreviewImage();
    this.renderPreviewContact();
    this.renderPreviewMediaKit();

    const dateline = this.previewDateline();
    if (preview.dateline) {
      preview.dateline.textContent = dateline;
      preview.dateline.classList.toggle('d-none', !dateline);
    }

    if (preview.body) {
      const scrubbed = scrubPreviewBody(this.dom.fields.body.value);
      const hasContent = !!(scrubbed.textContent.trim() || scrubbed.querySelector('img'));

      if (hasContent) {
        preview.body.innerHTML = scrubbed.innerHTML;
      } else {
        preview.body.textContent = preview.body.dataset.empty || '';
      }
      preview.body.classList.toggle('release-preview-empty', !hasContent);
    }
  };

  // The lead picture is whatever the media kit carries first. The uploader holds the live list
  // (uploads made in this session included, which pageData cannot know about); the saved order is
  // read only when there is no uploader at all. A file with no `url` is still in transit — it has
  // nothing to render yet.
  previewImage() {
    const uploaderImages = this.mediaGallery && this.mediaGallery.uploader
      ? this.mediaGallery.uploader.files.images
      : null;

    if (uploaderImages) {
      return uploaderImages.find(file => file.inMediaKit && file.url) || null;
    }

    return (pageData.order.images || []).find(image => image.url) || null;
  };

  // The press contact is three fields, so the tile lists the filled ones in form order, one per
  // line (CSS keeps the breaks). A release written before the split has the three empty and its
  // own free text in the read-only note above them — that text is the fallback, shown as typed.
  // With neither, the tile steps out of the release entirely: an empty "Media contact" box would
  // read as a section they forgot rather than one not reached.
  renderPreviewContact() {
    const preview = this.dom.preview;
    if (!preview.contact || !preview.contactBody) return;

    const lines = CONTACT_FIELDS
      .map(name => (this.dom.fields[name] ? this.dom.fields[name].value : '').trim())
      .filter(value => value);

    const legacy = this.dom.legacyContact ? (this.dom.legacyContact.dataset.legacyContact || '').trim() : '';
    // "Do not publish this contact info publicly online" keeps the contact out of the WordPress
    // post (PublishReleasesService), so the preview of that post leaves it out as well.
    const notPublic = this.dom.fields.contact_info_not_public;
    const contact = notPublic && notPublic.checked ? '' : (lines.length ? lines.join('\n') : legacy);

    preview.contactBody.textContent = contact;
    preview.contact.classList.toggle('d-none', !contact);
  };

  // The server drops the legacy free text once a save carries a complete contact — name and email
  // (Release#clear_legacy_contact) — so after such a save the note, and the preview fallback that
  // reads it, would show text that no longer exists. Mirror the server's rule and remove it.
  dropLegacyContactNote() {
    if (!this.dom.legacyContact) return;
    // Same completeness rule as the server: name AND email. A name-only save keeps the legacy
    // text in the DB, so the note has to stay on the page too.
    const filled = name => !!(this.dom.fields[name] && this.dom.fields[name].value.trim());
    if (!(filled('contact_name') && filled('contact_email'))) return;

    this.dom.legacyContact.remove();
    this.dom.legacyContact = null;
    this.renderPreviewContact();
  };

  // Everything the writer put in the media kit, images first, then the rest — the uploader's own
  // two buckets in its own order. It holds the live list, uploads made in this session included,
  // which pageData cannot know about; the saved order is read only when there is no uploader at
  // all. Unlike the lead image, a file without a `url` is kept: it is in the kit and belongs in
  // the list, it just has nothing to open yet, so the row renders without a link.
  previewMediaKitFiles() {
    const uploader = this.mediaGallery && this.mediaGallery.uploader;

    if (uploader) {
      return [...uploader.files.images, ...uploader.files.attachments].filter(file => file.inMediaKit);
    }

    const libraries = { images: pageData.order.images || [], attachments: pageData.order.attachments || [] };

    return (pageData.order.media_kit || [])
      .map(ref => (libraries[ref.library] || []).find(file => file.id === ref.id))
      .filter(Boolean)
      .map(API.parseFile.fromPageData);
  };

  // No "Download all (.zip)" here: zipping runs inside PublishReleasesService and has no route a
  // browser could reach, so the rows are the only way to a file. Every row is built from the
  // template element in the panel — the name is written as text, never interpolated as markup —
  // and the anchor is left without an `href` while the file has no URL, which renders it as the
  // plain text it is (the CSS link treatment is scoped to `[href]` for the same reason).
  renderPreviewMediaKit() {
    const preview = this.dom.preview;
    if (!preview.mediaKit || !preview.mediaKitList || !preview.mediaKitRowTpl) return;

    const files = this.previewMediaKitFiles();

    preview.mediaKitList.innerHTML = '';

    files.forEach(file => {
      const row = preview.mediaKitRowTpl.content.cloneNode(true);
      const icon = row.querySelector('.preview-media-kit-file-icon');
      const name = row.querySelector('.preview-media-kit-file-name');
      const meta = row.querySelector('.preview-media-kit-file-meta');

      icon.classList.add(`preview-media-kit-file-icon-${file.kind || 'file'}`);
      icon.querySelector('.mdi').classList.add(file.icon || 'mdi-file-document-outline');
      name.textContent = file.name || '';
      if (file.url) name.href = file.url;
      meta.textContent = file.meta || '';

      preview.mediaKitList.append(row);
    });

    preview.mediaKit.classList.toggle('d-none', !files.length);
  };

  renderPreviewImage() {
    const preview = this.dom.preview;
    if (!preview.media || !preview.mediaImage) return;

    const image = this.previewImage();

    if (image) {
      preview.mediaImage.src = image.url;
      preview.mediaImage.alt = image.name || '';
    } else {
      preview.mediaImage.removeAttribute('src');
      preview.mediaImage.alt = '';
    }

    preview.media.classList.toggle('d-none', !image);
  };

  arrowsEnableDisable(activeTab) {
    const disabled = {
      prev: false,
      next: false,
    };

    if (activeTab.parentElement.matches(':first-of-type')) {
      disabled.prev = true;
    } else if (activeTab.parentElement.matches(':last-of-type')) {
      disabled.next = true;
    }

    this.dom.arrowsPrev.forEach((elem) => {
      elem.disabled = disabled.prev;
    });
    this.dom.arrowsNext.forEach((elem) => {
      elem.disabled = disabled.next;
    });
  }

  // The rail opens one step at a time: any earlier step, the next one, nothing further (the click
  // handler in `initTabs`). The stylesheet draws that from `.active` alone (components/common/_tabs);
  // this states it for assistive technology, which cannot read a dashed circle.
  refreshLockedSteps() {
    const tabs = Object.values(this.dom.tabs);
    const activeIndex = tabs.findIndex(tab => tab.classList.contains('active'));

    tabs.forEach((tab, index) => {
      if (activeIndex > -1 && index > activeIndex + 1) {
        tab.setAttribute('aria-disabled', 'true');
      } else {
        tab.removeAttribute('aria-disabled');
      }
    });
  };

  // A click on a locked step used to do nothing and say nothing. It now answers under the step it
  // was aimed at, naming the step that does open, and takes the answer away again.
  showLockedStepHint(target, nextTab) {
    const template = (typeof pageData !== 'undefined' && pageData.lockedStepHint) || '';
    const nextTitle = nextTab && nextTab.querySelector('.tab-title');
    if (!template || !nextTitle) return;

    this.hideLockedStepHint();

    const hint = document.createElement('span');
    hint.className = 'tab-locked-hint';
    hint.setAttribute('role', 'status');
    hint.textContent = template.replace('{step}', nextTitle.textContent.trim());
    target.closest('.tab-link').appendChild(hint);

    this.lockedStepHint = hint;
    this.lockedStepHintTimer = setTimeout(() => this.hideLockedStepHint(), 2800);
  };

  hideLockedStepHint() {
    clearTimeout(this.lockedStepHintTimer);

    if (this.lockedStepHint) {
      this.lockedStepHint.remove();
      this.lockedStepHint = null;
    }
  };

  initTabs() {
    const initiallyActive = Object.values(this.dom.tabs).find(elem => elem.classList.contains('active'));
    this.arrowsEnableDisable(initiallyActive);
    this.refreshLockedSteps();
    this.gTagEvent('navigation', 'click - tab', initiallyActive.getAttribute('href').replace(/^#(.*?)(-panel)?$/, '$1'));

    $(Object.values(this.dom.tabs)).on('shown.bs.tab', e => {
      const active = e.currentTarget;
      const activeTab = active.getAttribute('href').replace(/^#(.*?)(-panel)?$/, '$1');
      this.gTagEvent('navigation', 'click - tab', activeTab);

      this.arrowsEnableDisable(active);
      this.refreshLockedSteps();
      this.hideLockedStepHint();
      document.body.classList.toggle('summary-hidden', activeTab === 'checkout')

      // The preview holds no state of its own — it is redrawn from the fields every time the step
      // is entered, so it can never show a manuscript the writer has already moved on from.
      if (activeTab === 'preview') {
        this.renderPreview();
      }

      if (activeTab === 'checkout') {
        this.refreshSummary();
        this.renderCheckout();
      }
    });

    $(Object.values(this.dom.tabs)).on('click', e => {
      e.preventDefault();
      const target = e.currentTarget;
      const targetTab = target.getAttribute('href').replace(/^#(.*?)(-panel)?$/, '$1');

      const activeEntry = Object.entries(this.dom.tabs).find(([key, item]) => item.classList.contains('active'));
      // const active = activeEntry[1];
      const activeTab = activeEntry && activeEntry[0];

      const tabs = Object.keys(this.dom.tabs);
      const activeTabIndex = tabs.indexOf(activeTab)
      const targetTabIndex = tabs.indexOf(targetTab)

      if (targetTabIndex < activeTabIndex) {
        $(target).tab('show');
      } else if (targetTabIndex === activeTabIndex + 1 && activeTab) {
        this.validateStep(activeTab).then(() => {
          $(target).tab('show');
        }).catch((err) => {
          window.debug && console.warn('validation failed for tab', activeTab, ':', err);
        });
      } else if (targetTabIndex > activeTabIndex + 1 && activeTab) {
        this.showLockedStepHint(target, this.dom.tabs[tabs[activeTabIndex + 1]]);
      }
    });

    this.arrowsDisabled = false;

    this.dom.arrowsNext.forEach((elem) => {
      elem.addEventListener('click', e => {
        e.preventDefault();
        const tabsArray = Object.values(this.dom.tabs);
        const currentlyActive = tabsArray.findIndex(elem => elem.classList.contains('active'));
        if (!this.arrowsDisabled && currentlyActive < tabsArray.length - 1) {
          this.arrowsDisabled = true; setTimeout(() => { this.arrowsDisabled = false }, 250);
          $(tabsArray[currentlyActive + 1]).trigger('click');
        }
      });
    });

    this.dom.arrowsPrev.forEach((elem) => {
      elem.addEventListener('click', e => {
        e.preventDefault();
        const tabsArray = Object.values(this.dom.tabs);
        const currentlyActive = tabsArray.findIndex(elem => elem.classList.contains('active'));
        if (!this.arrowsDisabled && currentlyActive > 0) {
          this.arrowsDisabled = true; setTimeout(() => { this.arrowsDisabled = false }, 250);
          $(tabsArray[currentlyActive - 1]).trigger('click');
        }
      });
    });
  };

  validateStep(activeStep) {
    // The preview collects nothing, so it gates nothing: leaving it is always allowed.
    if (activeStep === 'preview') return Promise.resolve();

    if (this.validationInProgress) return Promise.reject('validation in progress');
    this.validationInProgress = true;
    return new Promise((resolve, reject) => {
      const fields = Object.fromEntries(Object.entries(this.fields).filter(([key, item]) => item.tab === activeStep));

      this.refreshOrder();
      this.saveOrder('validate').then(() => {
        API.call({
          endpoint: API.endpoints.release.finalize,
          params: { id: this.dom.orderId.value },
          suffix: 'validate_only=true',
          data: { release: this.order.data },
        }).then(res => {
          resolve();
          this.validationInProgress = false;
        }).catch(e => {
          if (e && e.response && e.response.status === 422 && e.response.data && e.response.data.errors) {
            const errorFields = this.setErrors(e.response.data.errors, fields);
            if (errorFields.length) {
              this.notifier.show('validation-errors');
              this.scrollToFirstError();
              reject();
              this.validationInProgress = false;
              return;
            }
          }
          resolve();
          this.validationInProgress = false;
        });
      }).catch(e => {
        // The draft save itself can be rejected — the press-contact format and length
        // rules run on every save, not only at checkout — and this branch has to settle
        // the promise either way. Leaving it unsettled froze the wizard: `Next` did
        // nothing and `validationInProgress` stayed true until a reload.
        if (e && e.response && e.response.status === 422 && e.response.data && e.response.data.errors) {
          this.setErrors(e.response.data.errors, fields);
          this.notifier.show('validation-errors');
          this.scrollToFirstError();
        } else {
          window.debug && console.warn('API connection error (validate step):', e);
        }
        reject();
        this.validationInProgress = false;
      });
    });
  }

  handleCategoryFilter = (e) => {
    const field = e.currentTarget || e.target;
    if (!field) return;

    const value = field.value;
    if (value === this.categoryFilterValue) return;
    this.categoryFilterValue = value;
    this.categoryFilterPatterns = value.split(' ').filter(Boolean).map(str => new RegExp(str, 'i'));

    Array.from(this.dom.categories).forEach(card => {
      const isPlanRestricted = card.closest(this.dom.categoryParentSelector)
        ?.classList.contains('plan-restricted');
      const matched = !isPlanRestricted && (!this.categoryFilterPatterns.length || card.classList.contains('action-card-selected') || (card.dataset?.name && this.categoryFilterPatterns.some(pattern => pattern.test(card.dataset.name))));
      card.parentElement.classList.toggle('d-none', !matched);
    });

    Array.from(this.dom.categoryRegions).forEach(region => {
      const empty = region.querySelectorAll(`${this.dom.categoryParentSelector}:not(.d-none)`).length === 0;
      region.classList.toggle('d-none', empty);
    });

    // A search has to reach into every region, so a query opens them all and clearing it returns
    // to one open group.
    this.refreshRegionAccordions(!!this.categoryFilterPatterns.length);
  };

  handleCategoryClick = (e) => {
    e.preventDefault();
    const elem = e.currentTarget;
    if (elem.dataset.restrictedPlanId && elem.dataset.active === 'true') return;
    this.updatePageDataSelectedCategories(elem.dataset);
    const active = elem.dataset.active === 'true' ? false : true
    elem.dataset.active = active;
    elem.classList.toggle('action-card-selected', active);
    this.toggleCategory(elem.dataset);
    this.preventLeaving();

    this.fields['categories'].errors.forEach(err => {
      const index = this.order[this.fields['categories'].checkoutSection].errors.indexOf(err);
      if (index > -1) {
        this.order[this.fields['categories'].checkoutSection].errors.splice(index, 1);
      }
    });
    this.fields['categories'].errors = [];
    this.refreshErrors();
  };

  toggleCategory({ id, name, region }) {
    const index = this.order.categories.active.findIndex(cat => cat.id === id);
    if (index > -1) {
      this.order.categories.active.splice(index, 1);
    } else {
      this.order.categories.active.push({ id, name: name, region: region });
    }
    this.refreshSummary();
  };

  // Close one dialog and open the next only once the first is really gone. Every modal on this page
  // fades now, and Bootstrap 4 runs a dialog's teardown — removing the backdrop and releasing
  // `body.modal-open` — on `hidden.bs.modal`, i.e. after the 300ms fade. Showing the second one
  // inside that window lets the first one's teardown strip the second one's backdrop and scroll
  // lock. Waiting for `hidden` costs one fade and cannot race.
  handOverModal(from, to) {
    if (!from.classList.contains('show')) {
      $(to).modal('show');
      return;
    }

    $(from).one('hidden.bs.modal', () => $(to).modal('show'));
    $(from).modal('hide');
  };

  openInquiryModal = (e) => {
    e.preventDefault();

    if (!pageData.user.logged_in && !this.userSignedUp) {
      if (typeof $ !== 'undefined' && $('#authModal-saved').length) {
        $('#authModal').modal('show');
        !window.authCallback && (window.authCallback = {});
        window.authCallback.signup = () => {
          this.userSignedUp = true;
          this.updateReleaseIds();
          $(this.dom.modals.inquiry).modal('show');
        };
      }
    } else {
      $(this.dom.modals.inquiry).modal('show');
    }
  };

  submitInquiry = (e) => {
    e.preventDefault();

    try {
      const message = this.dom.inquiry.msg.value;
      if (message) {
        API.call({
          endpoint: API.endpoints.release.notifications,
          params: { releaseUrl: window.location.href.replace(/(\/releases\/[^/?]+)([/?].*)*$/, '$1') },
          data: {
            release_notification: {
              message,
              notification_type: 'custom_message'
            }
          },
        }).then(res => {
          Array.from(this.dom.modals.inquiryConfirmation.querySelectorAll('[data-href-pattern]')).forEach(link => {
            link.href = link.dataset.hrefPattern.replace(/\{id\}/g, this.dom.orderId.value);
          })

          this.handOverModal(this.dom.modals.inquiry, this.dom.modals.inquiryConfirmation);
        });
      }
    } catch (err) {
      console.warn('error sending comment', err);
    }
  };

  handleOptionClick = (e) => {
    e.preventDefault();
    const elem = e.currentTarget;
    const active = elem.dataset.active === 'true' ? false : true
    elem.dataset.active = active;
    elem.classList.toggle('action-card-selected', active);
    elem.classList.toggle('with-checkmark', active);
    this.toggleOption(elem.dataset);
    this.preventLeaving();
  };

  toggleOption({ id, name, price }) {
    const index = this.order.options.active.findIndex(elem => elem.name === name);
    if (index > -1) {
      this.order.options.active.splice(index, 1);
    } else {
      const { currentPlan } = this.getCurrentPlanDetails();
      let isIncluded;
      if (currentPlan.option_ids) {
        const optionId = parseInt(id, 10);
        isIncluded = currentPlan.option_ids.includes(optionId);
      }
      this.order.options.active.push({ id: id, name: name, price: isIncluded ? 0 : parseFloat(price) });
    }
    this.refreshSummary();
  };

  updatePageDataOrderWithPlan(plan) {
    pageData.order.basePrice = parseFloat(plan.price);
    pageData.order.currentPlanId = plan.id;
    pageData.order.freeCategories = plan.free_categories;
    pageData.order.firstReleaseBasePrice = plan.first_release_discount_price;
  };

  updatePageDataSelectedCategories(category) {
    let foundIndex = pageData.order.selectedRegionsWithPublishingTime.findIndex(item =>
      item.category_name === category.name && item.region_name === category.region
    );

    if (foundIndex !== -1) {
      pageData.order.selectedRegionsWithPublishingTime.splice(foundIndex, 1);
    } else {
      const newRegionWithTime = {
        category_name: category.name,
        publishing_time: category.publishingTime,
        region_name: category.region,
        region_time_zone: category.timeZone
      };
      pageData.order.selectedRegionsWithPublishingTime.push(newRegionWithTime);
    }

    const datePickerInput = document.getElementById('datetime');
    if (datePickerInput) {
      updateDatePickerInfo(datePickerInput);
    } else {
      console.error('Date picker input not found');
    }
  };

  handlePlanClick = (e) => {
    e.preventDefault();
    const elem = e.currentTarget;

    this.dom.plans.forEach(plan => {
      plan.dataset.active = 'false';
      plan.classList.remove('action-card-selected', 'with-checkmark');
    });

    // make active only the choosen plan
    elem.dataset.active = 'true';
    elem.classList.add('action-card-selected', 'with-checkmark');

    const selectedPlan = pageData.plans.find(plan => plan.id === parseInt(elem.dataset.id, 10));

    if (selectedPlan) {
      this.updatePageDataOrderWithPlan(selectedPlan);
    }

    this.togglePlan(elem.dataset);
    this.applyCategoryRestrictions(parseInt(elem.dataset.id, 10));
    this.preventLeaving();
  };

  // "Included" chip + struck-through price on every option the plan already pays for.
  //
  // Its own pass over ALL option cards: the chip has to come off the cards the previous plan
  // included as surely as it goes onto the new plan's. Marking is presentation only — an included
  // option stays clickable and deselectable (owner decision, 2026-08-21).
  syncIncludedOptionCards(includedOptionIds) {
    this.dom.options.forEach(option => {
      const optionId = parseInt(option.dataset.id, 10);
      option.classList.toggle('option-included', includedOptionIds.includes(optionId));
    });
  };

  // The ids of the options a plan includes, read from the same `pageData.plans` the price reads.
  includedOptionIds(planId) {
    const plan = pageData.plans.find(candidate => candidate.id === parseInt(planId, 10));

    return (plan && plan.option_ids) || [];
  };

  setOptionCardSelected(option, selected) {
    option.dataset.active = selected;
    option.classList.toggle('action-card-selected', selected);
    option.classList.toggle('with-checkmark', selected);
  };

  togglePlan({ id, name, price, optionIds }) {
    // What the plan being left brought along. Empty on hydration — no plan was chosen before the
    // saved one — so opening a saved order deselects nothing. It does re-select whatever the saved
    // plan includes, as it always has.
    const previousPlan = this.order.plans.active[0];
    const leftBehind = previousPlan ? this.includedOptionIds(previousPlan.id) : [];

    this.order.plans.active = [];
    this.order.plans.active.push({ id: id, name: name, price: parseFloat(price) });

    const optionIdsArray = optionIds
      ? optionIds.split(',').map(Number).filter(optionId => !Number.isNaN(optionId))
      : [];

    this.syncIncludedOptionCards(optionIdsArray);

    // One pass over every add-on, three cases. The new plan's own are selected at no charge. The
    // ones the previous plan included and this one does not are deselected — a plan takes its
    // add-ons with it instead of leaving them behind as paid extras (owner decision, 2026-10-13).
    // Anything else that is selected is the customer's own choice and stays, at its full price.
    // Who selected an add-on is not tracked: one the customer picked and a later plan then
    // included leaves with that plan like the rest.
    this.dom.options.forEach(option => {
      const optionId = parseInt(option.dataset.id, 10);
      const index = this.order.options.active.findIndex(opt => opt.id === option.dataset.id);
      const entry = { id: option.dataset.id, name: option.dataset.name };

      if (optionIdsArray.includes(optionId)) {
        this.setOptionCardSelected(option, true);

        if (index > -1) {
          this.order.options.active[index] = { ...entry, price: 0 };
        } else {
          this.order.options.active.push({ ...entry, price: 0 });
        }
      } else if (leftBehind.includes(optionId)) {
        this.setOptionCardSelected(option, false);

        if (index > -1) {
          this.order.options.active.splice(index, 1);
        }
      } else if (index > -1) {
        this.order.options.active[index] = { ...entry, price: parseFloat(option.dataset.price) };
      }
    });

    this.preventLeaving();
    this.refreshSummary();
  };

  applyCategoryRestrictions = (planId) => {
    const categoriesContainer = this.dom.wrapper.querySelector('.categories');
    const planIdStr = planId ? String(planId) : null;

    Array.from(this.dom.categories).forEach(card => {
      const restrictedPlanId = card.dataset.restrictedPlanId;
      const parent = card.closest(this.dom.categoryParentSelector);

      if (!restrictedPlanId) return;

      if (restrictedPlanId !== planIdStr) {
        parent.classList.add('d-none', 'plan-restricted');
        if (card.dataset.active === 'true') {
          card.dataset.active = 'false';
          card.classList.remove('action-card-selected');
          const idx = this.order.categories.active.findIndex(cat => cat.id === card.dataset.id);
          if (idx > -1) {
            this.order.categories.active.splice(idx, 1);
          }
          this.updatePageDataSelectedCategories(card.dataset);
        }
      } else {
        parent.classList.remove('d-none', 'plan-restricted');
        if (card.dataset.active !== 'true') {
          card.dataset.active = 'true';
          card.classList.add('action-card-selected');
          const exists = this.order.categories.active.findIndex(cat => cat.id === card.dataset.id);
          if (exists === -1) {
            this.order.categories.active.push({
              id: card.dataset.id,
              name: card.dataset.name,
              region: card.dataset.region
            });
          }
          this.updatePageDataSelectedCategories(card.dataset);
        }
      }
    });

    Array.from(this.dom.categoryRegions).forEach(region => {
      const visible = region.querySelectorAll(
        `${this.dom.categoryParentSelector}:not(.d-none)`
      ).length;
      region.classList.toggle('d-none', visible === 0);

      const hasMatchingRestricted = region.querySelector(
        `[data-type="category"][data-restricted-plan-id="${planIdStr}"]`
      );
      if (hasMatchingRestricted && planIdStr) {
        const firstRegion = categoriesContainer.querySelector('.region-group:not(.d-none)');
        if (firstRegion && firstRegion !== region) {
          categoriesContainer.insertBefore(region, firstRegion);
        }
      }
    });

    this.refreshRegionAccordions();
    this.refreshSummary();
  };

  handlePaymentTypeClick = (e) => {
    e.preventDefault();
    const elem = e.currentTarget;
    elem.dataset.active = true;
    this.dom.paymentMethods.forEach(item => {
      elem.dataset.active = item === elem;
      item.classList.toggle('action-card-selected', item === elem);
    })
    this.setPaymentMethod(elem.dataset.value);
    this.preventLeaving();
  };

  setPaymentMethod = (chosen) => {
    const isManualPayment = chosen === 'postpaid';

    this.order.manual_payment.value = isManualPayment;
    this.dom.invoiceData.classList.toggle('d-none', !isManualPayment);

    // The lock caption names the card processor, so it follows the same switch as the invoice block.
    if (this.dom.paymentSecured) {
      this.dom.paymentSecured.classList.toggle('d-none', isManualPayment);
    }
  }

  handleTermsCheckbox = () => {
    this.dom.finalizeOrder.classList.toggle('disabled', !this.isConsentAccepted());
    this.refreshFinalizeLabel();

    this.validateTermsCheckbox();
  };

  isConsentAccepted() {
    return !!this.dom.fields.terms_accepted?.checked;
  };

  // The checkout gate. It has always been client-side only — none of the consent params is a model
  // attribute or reaches the server — and since the box states the three acknowledgments as a list
  // under one checkbox, there is one control to read.
  validateTermsCheckbox = () => {
    const accepted = this.isConsentAccepted();

    this.fields.terms_accepted.errors.splice(0);
    if (!accepted) {
      this.fields.terms_accepted.errors.push(true);
    }

    this.refreshErrors();

    return accepted;
  };

  handleSaveClick = (e) => {
    e.preventDefault();
    this.saveOrder('save-draft');
  };

  handleSignupClick = (e) => {
    e.preventDefault();
    this.saveOrder('signup');
  };

  handleFinalizeClick = (e) => {
    e.preventDefault();

    if (this.validateTermsCheckbox()) {
      this.finalizeOrder();
    }
  };

  getCurrentPlanDetails = () => {
    const currentPlan = pageData.plans.find(plan => plan.id === pageData.order.currentPlanId);
    const planBasePrice = currentPlan ? currentPlan.price : 0;
    return { currentPlan, planBasePrice };
  };

  calculateTotalNet() {
    const { planBasePrice } = this.getCurrentPlanDetails();

    const { firstReleaseBasePrice, applicableForFirstReleaseDiscount } = pageData.order;
    const { extraServices, categories, options } = this.order;
    const totalBasePrice = (applicableForFirstReleaseDiscount ? firstReleaseBasePrice : planBasePrice) + extraServices.priceVal + categories.price + options.price;
    return totalBasePrice;
  }

  refreshSummary() {
    const { currentPlan, planBasePrice } = this.getCurrentPlanDetails();
    if (currentPlan) {
      this.updatePageDataOrderWithPlan(currentPlan);
    }

    this.order.content.price = pageData.order.applicableForFirstReleaseDiscount ? pageData.order.firstReleaseBasePrice : planBasePrice;
    this.order.categories.price = Math.max(0, this.order.categories.active.length - pageData.order.freeCategories) * pageData.order.categoryPrice;
    this.order.options.price = this.order.options.active.reduce((sum, elem) => sum + elem.price, 0);
    this.order.total.net = this.calculateTotalNet();
    this.order.total.tax = this.order.total.net * (pageData.order.tax || 0);
    this.order.total.gross = this.order.total.net + this.order.total.tax;
    this.order.total.grossNum = this.order.total.gross;
    this.order.publish_at.date = this.dom.fields.publish_at.value && momentToRemote(parsePublishAtInput(this.dom.fields.publish_at.value));
    this.order.publish_at.date_long = this.dom.fields.publish_at.value && momentToRemoteLong(parsePublishAtInput(this.dom.fields.publish_at.value));
    this.order.categories.activeAndGroupedByRegion = this.order.categories.active.reduce((acc, { region, name }) => {
      acc[region] = acc[region] || [];
      acc[region].push(name);
      return acc;
    }, {});

    // The checkout summary's regions row says how much of the selection the plan already covers.
    this.order.categories.selectedCount = this.order.categories.active.length;
    this.order.categories.includedCount = Math.min(this.order.categories.selectedCount, pageData.order.freeCategories || 0);

    // There, every add-on is a line of its own with its own amount. A plan-included one carries
    // price 0 (toggleOption) and reads "(included)" rather than "¥0". The amounts are formatted
    // here because renderSummary() only formats the order's own top-level price keys, not the
    // per-add-on ones.
    this.order.options.active.forEach(option => {
      option.included = option.price === 0;
      option.priceLabel = option.included ? '' : formatCurrency(option.price);
    });

    // Before renderSummary(), which replaces the numeric prices with formatted strings.
    this.refreshCommerceMeta(this.order.options.price);
    this.refreshRegionCounts();
    this.refreshZeroTotalState();
    this.refreshFinalizeLabel();
    this.refreshTierChip();

    this.renderSummary();
  };

  currencyKeys = ['price', 'net', 'tax', 'gross'];

  renderSummary() {
    Object.keys(this.order).forEach(key => {
      this.currencyKeys.forEach(currencyKey => {
        if (typeof this.order[key][currencyKey] === 'number') {
          this.order[key][currencyKey] = formatCurrency(this.order[key][currencyKey]);
        }
      });
    });

    renderTpl(this.templates.summary, this.order);
    const tabTriggers = this.templates.summary.target.querySelectorAll('[data-target-tab]');

    for (let trigger of tabTriggers) {
      trigger.addEventListener('click', e => {
        $(this.dom.tabs[e.currentTarget.getAttribute('data-target-tab')]).tab('show');
      });
    }
  };

  renderCheckout() {
    renderTpl(this.templates.checkout, this.order);

    const tabTriggers = this.templates.checkout.target.querySelectorAll('[data-target-tab]');

    for (let trigger of tabTriggers) {
      trigger.addEventListener('click', e => {
        $(this.dom.tabs[e.currentTarget.getAttribute('data-target-tab')]).tab('show');
      });
    }
  };

  refreshOrder() {
    this.refreshSummary();

    this.order.save_contact_info_in_user.value = this.dom.fields.save_contact_info_in_user.checked;
    this.order.contact_info_not_public.value = this.dom.fields.contact_info_not_public.checked;

    this.order.data = {
      title: this.dom.fields.title.value,
      introduction: this.dom.fields.introduction.value,
      body: this.dom.fields.body.value,
      contact_name: this.dom.fields.contact_name.value,
      contact_phone: this.dom.fields.contact_phone.value,
      contact_email: this.dom.fields.contact_email.value,
      publish_at: this.dom.fields.publish_at.value && momentToAPI(parsePublishAtInput(this.dom.fields.publish_at.value)),
      skip_approval: !this.dom.fields.skip_approval.checked,
      contact_info_not_public: this.order.contact_info_not_public.value,
      plan_id: pageData.order.currentPlanId,
      // total_price: this.order.total.price,
      categories: this.order.categories.active.map(cat => cat.id),
      options: this.order.options.active.map(item => item.id),
      ...(this.mediaGallery && this.mediaGallery.getFilesForShrine()),
      // A ¥0 order has no bank transfer to await. create_free_payment wipes the flag at
      // checkout anyway, but drafts saved in between must not read as "awaiting a
      // transfer" in the admin (AdminAbility, the payments column, the invoice panel).
      manual_payment: this.order.manual_payment.value && !this.isZeroTotalOrder(),
      translation_enabled: this.order.translation_enabled.value,
    };

    if (this.order.data.manual_payment) {
      Object.assign(this.order.data, {
        invoice_data_company_name: this.dom.fields.invoice_data_company_name.value,
        invoice_data_department: this.dom.fields.invoice_data_department.value,
        invoice_data_full_name: this.dom.fields.invoice_data_full_name.value,
        invoice_data_location: this.dom.fields.invoice_data_location.value,
        invoice_data_phone: this.dom.fields.invoice_data_phone.value,
        invoice_data_email: this.dom.fields.invoice_data_email.value,
        save_invoice_data_in_user: this.dom.fields.save_invoice_data_in_user.checked,
      });
    }
  };

  updateReleaseIds(release) {
    if (release) {
      window.currentRelease = {
        id: release.id,
        public_id: release.public_id,
      };
    }

    const activeIdType = pageData.user.logged_in || this.userSignedUp ? 'id' : 'public_id';
    window.currentRelease.activeIdType = activeIdType;

    if (this.dom.orderId.value !== window.currentRelease[activeIdType]) {
      history.pushState(null, document.title, `${localePrefix()}/releases/${window.currentRelease[activeIdType]}/edit`);
      this.dom.orderId.value = window.currentRelease[activeIdType];
    }
  };

  saveOrder(source) {
    return new Promise((resolve, reject) => {
      this.dom.saveDraft.disabled = true;
      this.dom.saveDraft.classList.remove('error');

      if (this.dom.orderId.value === '') {
        if ((source === 'save-draft') && this.notifier) {
          this.notifier.show('saving-progress', { progress: 25 });
        }
        API.call({
          endpoint: API.endpoints.release.create,
          data: { release: { title: '' } },
        }).then(res => {
          try {
            Cookies.set('releaseId', res.data.release.public_id, { expires: 30 });
            this.updateReleaseIds(res.data.release);
            // A first save is create + update: this call re-enters with an id, so the branch below
            // is the one that carries the manuscript and reports the saved state to the editor bar.
            this.saveOrder(source).then(resolve).catch(reject);
            // todo: display notification
          } catch (e) {
            this.dom.saveDraft.classList.add('error');
            console.warn('API error (try create release):', e);
            // todo: display notification
            reject(e);
          };
          this.dom.saveDraft.disabled = false;
        }).catch(e => {
          this.dom.saveDraft.disabled = false;
          this.dom.saveDraft.classList.add('error');
          source === 'save-draft' && this.notifier.show('saving-error');
          console.warn('API connection error (create release):', e);
          // todo: display notification
          reject(e);
        });
      } else {
        this.refreshOrder();

        if ((source === 'save-draft') && this.notifier) {
          this.notifier.show('saving-progress', { progress: 50 });
        }

        API.call({
          endpoint: API.endpoints.release.update,
          params: { id: this.dom.orderId.value },
          data: {
            release: {
              ...this.order.data,
              ...(source === 'upload' && { save_only_files: true }),
              ...(this.order.save_contact_info_in_user.value && { save_contact_info_in_user: this.order.save_contact_info_in_user.value }),
            },
          },
        }).then(res => {
          this.dom.saveDraft.disabled = false;
          source !== 'upload' && this.preventLeaving(false);
          if (source === 'save-draft') {
            this.notifier && this.notifier.show('saving-success');
            this.showDraftSaved();
          }
          this.updateReleaseIds(res.data.release);
          // A completed-saves counter for end-to-end tests. The "draft saved" label is sticky, so
          // it cannot tell a finished save from the previous one; a number that only grows can.
          // Incremented here, after the leave-guard and the status have been updated, so a test
          // that sees it grow knows the whole success path ran.
          // Only the "Save draft" button's own save counts: an upload's PUT finishing first must
          // not look like the save the test just asked for.
          if (source === 'save-draft') {
            document.documentElement.dataset.creatorSaves =
              String((parseInt(document.documentElement.dataset.creatorSaves, 10) || 0) + 1);
          }
          this.dropLegacyContactNote();
          if (
            !pageData.user.logged_in && !this.userSignedUp
            && source !== 'signup' && source !== 'upload' && source !== 'upload-with-data' && source !== 'validate'
            && typeof $ !== 'undefined' && $('#authModal-saved').length
          ) {
            $('#authModal-saved').modal('show');
            !window.authCallback && (window.authCallback = {});
            window.authCallback.signup = () => {
              this.userSignedUp = true;
              this.updateReleaseIds();
              this.finalizeOrder();
            };
          }
          // todo: display notification
          resolve(res.data);
        }).catch(e => {
          this.dom.saveDraft.disabled = false;
          this.dom.saveDraft.classList.add('error');
          source === 'save-draft' && this.notifier.show('saving-error');
          window.debug && console.warn('API connection error (update release):', e);
          // todo: display notification
          reject(e);
          if (e && e.response && e.response.status === 422 && e.response.data && e.response.data.errors) {
            this.setErrors(e.response.data.errors);
          }
        });
      }
    });
  };

  finalizeOrder() {
    this.dom.finalizeOrder.disabled = true;
    this.dom.finalizeOrder.classList.remove('error');
    this.showCheckoutRefusal('');

    this.refreshOrder();
    this.saveOrder('finalize').then(() => {
      // todo: validate
      if (!pageData.user.logged_in && !this.userSignedUp) {
        this.dom.finalizeOrder.disabled = false;
      } else {
        API.call({
          endpoint: API.endpoints.release.finalize,
          params: { id: this.dom.orderId.value },
          data: { release: this.order.data },
        }).then(res => {
          this.dom.finalizeOrder.disabled = false;
          // A release with an order went back to the team without a payment (CheckoutService).
          if (res.data.outcome === 'resubmitted') {
            this.showResubmitted(res.data.message);
            return;
          }
          // The server decides how the order was taken, and it is asked first: a ¥0 order has no
          // PaymentIntent, and Stripe rejects a zero amount, so the free branch has to come before
          // any Stripe code runs.
          if (res.data.payment.payment_method === "free") {
            this.showFreeSuccess();
          } else if (res.data.payment.payment_method === "manual") {
            this.showTransferDetails();
          } else {
            try {
              this.startPayment(res.data.payment);
            } catch (e) {
              console.warn('payment info error:', e);
            }
          }
        }).catch(e => {
          this.dom.finalizeOrder.disabled = false;
          this.dom.finalizeOrder.classList.add('error');
          // console.warn('API connection error (finalize release):', Object.entries(e));
          if (e && e.response && e.response.status === 422 && e.response.data && e.response.data.errors) {
            this.setErrors(e.response.data.errors);
            this.notifier.show('validation-errors');
            this.scrollToFirstError();
          }
          // A checkout that took nothing (401, 409, 503): the server's sentence, as it is.
          if (e && e.response && [401, 409, 503].includes(e.response.status) && e.response.data && e.response.data.message) {
            this.showCheckoutRefusal(e.response.data.message);
          }
        });
      }
    }).catch(e => {
      this.dom.finalizeOrder.disabled = false;
      this.dom.finalizeOrder.classList.add('error');
      // The release left Draft in another tab (409): the save is refused, the server says why.
      const data = e && e.response && e.response.data;
      data && data.message && this.showCheckoutRefusal(data.message);
      window.debug && console.warn('API connection error (update release):', e);
    });
  };

  // Brings the first rendered error of the active panel into view — a failed "Next" while the
  // reader is at the top of a long step would otherwise show no visible change at all.
  scrollToFirstError() {
    const panel = this.dom.wrapper.querySelector('.tab-pane.active');
    if (!panel) return;
    const target = Array.from(panel.querySelectorAll('.error-msg')).find(el => el.textContent.trim())
      || panel.querySelector('.error');
    target && target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  setErrors(errors, providedFields) {
    const checkedFields = providedFields || this.fields;
    const handleCheckout = !providedFields;

    const output = [];

    handleCheckout && Object.keys(this.order).forEach(key => this.order[key].errors = []);
    Object.keys(checkedFields).forEach(key => checkedFields[key].errors = []);

    Object.entries(errors).forEach(([name, msgs]) => {
      if (typeof checkedFields[name] !== 'undefined') {
        checkedFields[name].errors.push(...msgs);
        output.push(name);
        if (handleCheckout && typeof this.order[checkedFields[name].checkoutSection] !== 'undefined') {
          this.order[checkedFields[name].checkoutSection].errors.push(...msgs);
        }
      }
    });

    this.refreshErrors();
    return output;
  };

  refreshErrors() {
    // const tabsWithErrors = [];

    Object.entries(this.fields).forEach(([name, fieldConfig]) => {
      const elem = this.dom.fields[name];
      elem && elem.classList.toggle('error', fieldConfig.errors.length);
      // fieldConfig.errors.length && tabsWithErrors.push(fieldConfig.tab);
      if (elem && elem.id) {
        Array.from(this.dom.wrapper.querySelectorAll(`[for="${elem.id}"]`)).forEach(label => {
          label.classList.toggle('error', fieldConfig.errors.length);
        })
      }
    });

    // Object.entries(this.dom.tabs).forEach(([name, elem]) => {
    //   elem.classList.toggle('error', tabsWithErrors.includes(name));
    // });

    Object.entries(this.dom.errorMsgs).forEach(([name, elem]) => {
      elem.innerHTML = this.fields[name].errors.length ? this.fields[name].errors.join(' ') : '';
    });

    this.renderCheckout();
  };

  // A ¥0 order is already submitted by the time the response arrives — there is no provider step
  // left, so this modal is the end of the flow rather than the start of a payment.
  showFreeSuccess() {
    this.gTagEvent('user action', 'order completed', 'free', this.order.total.grossNum);

    Array.from(this.dom.modals.freeSuccess.querySelectorAll('[data-href-pattern]')).forEach(link => {
      link.href = link.dataset.hrefPattern.replace(/\{id\}/g, this.dom.orderId.value);
    })
    $(this.dom.modals.freeSuccess).modal('show');
  }

  // The release had an order and went back to the team without a payment; the sentence names the
  // amounts (CheckoutService, the server's `message`).
  showResubmitted(message) {
    this.gTagEvent('user action', 'order completed', 'resubmitted', 0);

    const modal = this.dom.modals.resubmitted;
    modal.querySelector('[data-resubmitted-message]').textContent = message;
    Array.from(modal.querySelectorAll('[data-href-pattern]')).forEach(link => {
      link.href = link.dataset.hrefPattern.replace(/\{id\}/g, this.dom.orderId.value);
    });
    $(modal).modal('show');
  }

  showCheckoutRefusal(message) {
    if (!this.dom.checkoutRefusal) return;

    this.dom.checkoutRefusal.textContent = message;
    message && this.dom.checkoutRefusal.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showTransferDetails() {
    const data = Object.assign({}, this.order, {
      id: this.dom.orderId.value,
    });

    this.gTagEvent('user action', 'order completed', 'invoice payment', this.order.total.grossNum);

    Array.from(this.dom.modals.bankTransfer.querySelectorAll('[data-href-pattern]')).forEach(link => {
      link.href = link.dataset.hrefPattern.replace(/\{id\}/g, this.dom.orderId.value);
    })
    $(this.dom.modals.bankTransfer).modal('show');
  }

  startPayment(receivedIntent) {
    this.startWalletPayment(receivedIntent);
    this.startCardPayment(receivedIntent);

    // The card button names what this intent charges: the page's summary is priced from the page's
    // first-release flag, which a payment of another release may have made stale (D-044).
    const payButton = this.dom.stripe.form.querySelector('button');
    if (payButton.dataset.labelAmount && Number.isFinite(receivedIntent.amount)) {
      payButton.textContent = payButton.dataset.labelAmount.replace('{amount}', formatCurrency(receivedIntent.amount));
    }

    // Leaving the card window unlocks the release, so the customer can edit and check out again
    // (the server cancels the open intent first). Not while a card is being confirmed (3-D Secure in
    // its own frame), and not after a success.
    if (!this.cardWindowBound) {
      this.cardWindowBound = true;
      $(this.dom.modals.stripe).on('hidden.bs.modal', () => {
        if (this.paymentSucceeded) return;
        // Closed while a card is being confirmed: unlock once the confirmation fails, never before.
        if (this.confirmingPayment) {
          this.unlockAfterConfirm = true;
          return;
        }
        this.unlockFor(this.cardPayment.receivedIntent);
      });
    }
    $(this.dom.modals.stripe).modal('show');
  };

  // Locks the release for the payment the page is about to confirm. The server refuses a payment that
  // is no longer the release's current one (an edit in another tab): the window closes and says so.
  lockFor(receivedIntent) {
    return API.call({
      endpoint: API.endpoints.release.lock,
      params: { id: this.dom.orderId.value },
      data: { payment_id: receivedIntent.id },
    }).catch(e => {
      const data = e && e.response && e.response.data;
      if (data && data.message) {
        // Closing sends an unlock the server ignores: this payment never locked anything.
        $(this.dom.modals.stripe).modal('hide');
        this.showCheckoutRefusal(data.message);
        this.refreshOrder();
      }
      throw e;
    });
  }

  // A confirmation ended (`succeeded` false on an error or a rejected promise). A window closed
  // meanwhile unlocks now, for the payment that was being confirmed.
  finishConfirm(receivedIntent, succeeded) {
    this.confirmingPayment = false;
    if (this.unlockAfterConfirm && !succeeded) this.unlockFor(receivedIntent);
    this.unlockAfterConfirm = false;
  }

  unlockFor(receivedIntent) {
    API.call({
      endpoint: API.endpoints.release.unlock,
      params: { id: this.dom.orderId.value },
      data: { payment_id: receivedIntent && receivedIntent.id },
    }).catch(e => {
      const data = e && e.response && e.response.data;
      data && data.message && this.showCheckoutRefusal(data.message);
    });
  }

  startWalletPayment(receivedIntent) {
    if (typeof this.walletPayment === 'undefined') {
      this.walletPayment = {};
      this.walletPayment.receivedIntent = receivedIntent;

      this.walletPayment.paymentRequest = this.stripe.paymentRequest({
        country: 'JP',
        currency: 'jpy',
        total: {
          label: 'Total',
          amount: this.walletPayment.receivedIntent.amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      this.walletPayment.prButton = this.stripeElements.create('paymentRequestButton', {
        paymentRequest: this.walletPayment.paymentRequest,
        style: {
          paymentRequestButton: {
            // type: 'buy', // One of 'default', 'book', 'buy', or 'donate'
            theme: 'light-outline', // One of 'dark', 'light', or 'light-outline' // Defaults to 'dark'
            height: '50px', // Defaults to '40px'. The width is always '100%'.
          },
        },
      });

      (async () => {
        // Check the availability of the Payment Request API first.
        const result = await this.walletPayment.paymentRequest.canMakePayment();
        if (result) {
          this.walletPayment.prButton.mount(this.dom.stripe.button);
          this.dom.stripe.buttonWrapper.style.display = 'block';
        } else {
          this.dom.stripe.buttonWrapper.style.display = 'none';
        }
      })();

      this.walletPayment.paymentRequest.on('paymentmethod', async (ev) => {
        const receivedIntent = this.walletPayment.receivedIntent;
        // The wallet locks the release like the card form does.
        try {
          await this.lockFor(receivedIntent);
        } catch (e) {
          ev.complete('fail');
          return;
        }

        // Confirm the PaymentIntent without handling potential next actions (yet).
        this.confirmingPayment = true;
        let confirmed;
        try {
          confirmed = await this.stripe.confirmCardPayment(
            receivedIntent.client_secret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );
        } catch (e) {
          ev.complete('fail');
          this.finishConfirm(receivedIntent, false);
          return;
        }
        const { paymentIntent, error: confirmError } = confirmed;

        if (confirmError) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete('fail');
          this.finishConfirm(receivedIntent, false);
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete('success');
          // Check if the PaymentIntent requires any actions and if so let Stripe.js
          // handle the flow. If using an API version older than "2019-02-11" instead
          // instead check for: `paymentIntent.status === "requires_source_action"`.
          if (paymentIntent.status === "requires_action") {
            // Let Stripe.js handle the rest of the payment flow.
            const { error } = await this.stripe.confirmCardPayment(receivedIntent.client_secret).
              catch(() => ({ error: true }));
            this.finishConfirm(receivedIntent, !error);
            if (error) {
              // The payment failed -- ask your customer for a new payment method.
              this.handlePaymentError();
            } else {
              // The payment has succeeded.
              this.handlePaymentSuccess();
            }
          } else {
            // The payment has succeeded.
            this.finishConfirm(receivedIntent, true);
            this.handlePaymentSuccess();
          }
        }
      });

    } else {
      this.walletPayment.receivedIntent = receivedIntent;

      this.walletPayment.paymentRequest.update({
        total: {
          label: 'Total',
          amount: this.walletPayment.receivedIntent.amount,
        },
      });

      /* dev note: probably not necessary, it's still the same object */
      // this.walletPayment.prButton.update({
      //   paymentRequest: this.walletPayment.paymentRequest,
      // });
    }
  };

  startCardPayment(receivedIntent) {
    if (typeof this.cardPayment === 'undefined') {

      this.cardPayment = {};
      this.cardPayment.receivedIntent = receivedIntent;

      this.cardPayment.card = this.stripeElements.create("card", { style: stripeStyle });
      this.cardPayment.card.mount(this.dom.stripe.card);

      this.cardPayment.card.on('change', (event) => {
        this.dom.stripe.cardErrors.textContent = event.error ? event.error.message : '';
      });

      this.dom.stripe.form.addEventListener('submit', (ev) => {
        ev.preventDefault();

        const receivedIntent = this.cardPayment.receivedIntent;
        this.lockFor(receivedIntent).then(() => {
          this.confirmingPayment = true;
          this.stripe.confirmCardPayment(receivedIntent.client_secret, {
              payment_method: {
                card: this.cardPayment.card,
                // billing_details: {
                //   name: 'John Doe'
                // }
              }
          }).then(result => {
            this.finishConfirm(receivedIntent, !result.error && result.paymentIntent.status === 'succeeded');
            if (result.error) {
              // Show error to your customer (e.g., insufficient funds)
              this.handlePaymentError(result.error.message);
            } else {
              // The payment has been processed!
              if (result.paymentIntent.status === 'succeeded') {
                // Show a success message to your customer
                // There's a risk of the customer closing the window before callback
                // execution. Set up a webhook or plugin to listen for the
                // payment_intent.succeeded event that handles any business critical
                // post-payment actions.

                this.handlePaymentSuccess();
              } else {
                // this case isn't well documented, but it seems this is an error
                console.warn('Error during payment process:', result);
                this.handlePaymentError();
              }
            }
          }).catch(e => {
            this.finishConfirm(receivedIntent, false);
            console.warn('Card confirmation error:', e);
          });

        }).catch(e => {
          console.warn('API connection error (lock release):', Object.entries(e));
          // Handle error if needed
        });
      });

    } else {

      this.cardPayment.receivedIntent = receivedIntent;

    }
  };

  handlePaymentSuccess() {
    this.paymentSucceeded = true;
    this.gTagEvent('user action', 'order completed', 'paid via PayPal', this.order.total.grossNum);
    this.dom.stripe.form.classList.add('d-none');

    Array.from(this.dom.stripe.success.querySelectorAll('[data-href-pattern]')).forEach(link => {
      link.href = link.dataset.hrefPattern.replace(/\{id\}/g, this.dom.orderId.value);
    })

    this.dom.stripe.success.classList.remove('d-none');
  };

  // A declined card stays in the window: the intent goes back to `requires_payment_method` and the
  // same form retries it. Leaving the window unlocks (startPayment).
  handlePaymentError(msg) {
    this.dom.stripe.cardErrors.textContent = msg || '';
  }
};